import React from 'react'
import { useNavigate } from 'react-router-dom'
import { gql, useMutation } from 'urql'
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Link
} from '@mui/material'
import { AccountCircle } from '@mui/icons-material'
import theme from '../theme'
import { UserAuthResponse } from '../../types/campaign'

interface Props {
  user: UserAuthResponse | null
  backgroundColor?: string
}

const logoutMutation = gql`
  mutation Logout {
    logout
  }
`

const AppHeader = ({
  user,
  backgroundColor = theme.palette.background.default
}: Props) => {
  const navigate = useNavigate()
  const [, logout] = useMutation(logoutMutation)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      const result = await logout()
      if (result.data?.logout) {
        localStorage.clear()
        navigate('/')
        window.location.reload()
      }
    } catch (error) {
      console.error('Logout Error:', error)
    }
  }

  return (
    <AppBar
      position="static"
      color="inherit"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        padding: '0 40px',
        boxShadow: 'none',
        '&.MuiAppBar-root': {
          backgroundColor
        }
      }}
    >
      <Toolbar
        sx={{
          padding: 0,
          '&.MuiToolbar-root': {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            height: '28px',
            width: '100%',
            justifyContent: 'space-between'
          }}
        >
          <img
            src="/images/grocery-tv-banner-logo.svg"
            alt="Grocery TV"
            style={{ height: '17px' }}
          />
          <Button
            color="inherit"
            sx={{
              boxShadow: 'none',
              padding: '10px 15px',
              borderRadius: '8px',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
            onClick={handleClick}
          >
            <AccountCircle
              sx={{ color: theme.palette.text.primary, marginRight: '4px' }}
            />
          </Button>
          <Menu
            id="nav-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: -2,
              horizontal: 'right'
            }}
            MenuListProps={{
              disablePadding: true
            }}
            sx={{
              transform: 'translateX(-3px)',
              '& .MuiPaper-root': {
                paddingY: '12px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'primary.contrastText',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
              },
              '& .MuiMenuItem-root': {
                padding: '8px 16px',
                fontSize: '14px'
              }
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={theme.space['8']}
              padding="0 16px"
              marginBottom={theme.space['8']}
            >
              <Typography variant="body-4">{user?.name}</Typography>
              <Typography variant="body-4">{user?.email}</Typography>
            </Box>

            <MenuItem onClick={handleLogout}>Log out</MenuItem>
            <MenuItem onClick={handleClose}>
              <Link href="mailto:support@grocerytv.com" underline="none">
                <Box display="flex" flexDirection="column">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '12px',
                      color: theme.palette['sage-400'],
                      fontWeight: 600
                    }}
                  >
                    Need help?
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'hint.main'
                      }
                    }}
                  >
                    support@grocerytv.com
                  </Typography>
                </Box>
              </Link>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default AppHeader
